<app-header></app-header>

<main class="container mx-auto px-4 pb-6 lg:px-6 lg:py-8">
    <!-- Page Header -->
    <section class="mt-4 mb-6 lg:mb-8">
        <h1 class="text-2xl lg:text-3xl font-bold">FUNTEL STORIES WALLET</h1>
        <p class="text-wattpad-light mt-2">Manage your credits and earnings</p>
    </section>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-wattpad-primary"></div>
        <span class="ml-4">Loading wallet data...</span>
    </div>

    <!-- Wallet Content -->
    <div *ngIf="!isLoading">
        <!-- Wallet Tabs -->
        <div class="flex border-b border-gray-200 dark:border-gray-700 mb-6">
            <button 
                (click)="switchTab('reader')" 
                [class]="'wallet-tab py-3 px-4 font-medium text-center flex-1 ' + (activeTab === 'reader' ? 'active border-b-2 border-wattpad-primary text-wattpad-primary' : 'text-gray-500 hover:text-gray-700')">
                <i class="fas fa-book-reader mr-2"></i> Reader Wallet
            </button>
            <button 
                (click)="switchTab('writer')" 
                [class]="'wallet-tab py-3 px-4 font-medium text-center flex-1 ' + (activeTab === 'writer' ? 'active border-b-2 border-wattpad-primary text-wattpad-primary' : 'text-gray-500 hover:text-gray-700')">
                <i class="fas fa-pen-nib mr-2"></i> Writer Wallet
            </button>
            <button 
                (click)="switchTab('promotion')" 
                [class]="'wallet-tab py-3 px-4 font-medium text-center flex-1 ' + (activeTab === 'promotion' ? 'active border-b-2 border-wattpad-primary text-wattpad-primary' : 'text-gray-500 hover:text-gray-700')">
                <i class="fas fa-bullhorn mr-2"></i> Promotion
            </button>
        </div>

        <!-- Reader Wallet Content -->
        <div id="readerWallet" *ngIf="activeTab === 'reader'" class="wallet-content">
            <!-- Balance Summary -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
                <div class="flex justify-between items-center mb-4">
                    <h2 class="text-xl font-bold">Your Reading Wallet</h2>
                    <div class="relative">
                        <i class="fas fa-coins text-yellow-500 text-2xl"></i>
                    </div>
                </div>
                <div class="text-center py-4">
                    <p class="text-wattpad-light mb-1">Current Balance</p>
                    <p class="text-4xl font-bold text-wattpad-primary">₹{{readerWallet.balance}}</p>
                    <p class="text-sm text-wattpad-light mt-1">{{readerWallet.coins}} credits available</p>
                </div>
                <div class="text-center mt-4">
                    <button 
                        (click)="openRechargeModal()"
                        class="bg-wattpad-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-[#E5274B] transition-colors">
                        <i class="fas fa-plus-circle mr-2"></i>
                        Recharge Wallet
                    </button>
                </div>
            </div>

            <!-- Transaction History -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                <h2 class="text-xl font-bold mb-4">Transaction History</h2>
                
                <div class="overflow-x-auto" *ngIf="readerTransactions.length > 0">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="border-b border-gray-200 dark:border-gray-700">
                                <th class="text-left py-2">Date</th>
                                <th class="text-left py-2">Description</th>
                                <th class="text-right py-2">Amount</th>
                                <th class="text-right py-2">Credits</th>
                                <th class="text-right py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let transaction of readerTransactions" 
                                class="border-b border-gray-100 dark:border-gray-800">
                                <td class="py-3">{{formatDate(transaction.created_at)}}</td>
                                <td class="py-3">{{transaction.description || 'Recharge'}}</td>
                                <td class="py-3 text-right">₹{{transaction.amount}}</td>
                                <td class="py-3 text-right text-green-500">+{{transaction.coins_earned}}</td>
                                <td class="py-3 text-right">
                                    <span [class]="'px-2 py-1 text-xs rounded-full ' + 
                                                  (transaction.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 
                                                   transaction.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                                                   'bg-red-100 text-red-800')">
                                        {{transaction.payment_status}}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div *ngIf="readerTransactions.length === 0" class="text-center py-4 text-wattpad-light">
                    No transactions yet
                </div>
            </div>
        </div>

        <!-- Writer Wallet Content -->
        <div id="writerWallet" *ngIf="activeTab === 'writer'" class="wallet-content">
            <!-- Balance Summary -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
                <h2 class="text-xl font-bold mb-4">Writer Wallet Summary</h2>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p class="text-wattpad-light text-sm mb-1">Available Balance</p>
                        <p class="text-2xl font-bold text-wattpad-primary">₹{{writerWallet.balance}}</p>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p class="text-wattpad-light text-sm mb-1">Locked Balance</p>
                        <p class="text-2xl font-bold text-wattpad-accent">₹{{writerWallet.locked_balance}}</p>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p class="text-wattpad-light text-sm mb-1">Total Withdrawn</p>
                        <p class="text-2xl font-bold text-wattpad-secondary">₹{{writerWallet.total_withdrawn}}</p>
                    </div>
                </div>
                
                <!-- Transfer Funds Section -->
                <div class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg" *ngIf="writerWallet.balance > 0">
                    <h3 class="font-bold mb-2">Transfer Funds</h3>
                    
                    <!-- Transfer Type Dropdown -->
                    <div class="mb-3">
                        <label class="block text-sm font-medium mb-2">Transfer To</label>
                        <select 
                            [(ngModel)]="selectedTransferType"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-wattpad-primary focus:border-transparent dark:bg-gray-700"
                            (change)="onTransferTypeChange()">
                            <option value="">Select transfer type...</option>
                            <option value="reader">Transfer to Reader Wallet</option>
                            <option value="promotion">Transfer to Promotion Wallet</option>
                        </select>
                    </div>

                    <!-- Amount Input - Common for both types -->
                    <div class="flex gap-2">
                        <input 
                            type="number" 
                            [(ngModel)]="transferAmount"
                            [max]="writerWallet.balance"
                            min="1"
                            placeholder="Enter amount to transfer"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-wattpad-primary focus:border-transparent dark:bg-gray-700">
                        <button 
                            (click)="processTransfer()"
                            [disabled]="!isTransferValid() || isLoading"
                            class="px-4 py-2 bg-wattpad-primary text-white rounded-lg font-medium hover:bg-[#E5274B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {{ isLoading ? 'Transferring...' : 'Transfer' }}
                        </button>
                    </div>
                    
                    <div class="flex justify-between text-xs text-gray-600 mt-2">
                        <span>Max: ₹{{writerWallet.balance}}</span>
                        <span *ngIf="selectedTransferType === 'reader'">→ Converts to {{transferAmount}} coins</span>
                        <span *ngIf="selectedTransferType === 'promotion'">→ Directly added to promotion balance</span>
                    </div>
                </div>

                 <div class="flex justify-center space-x-4">
                    <button 
                        (click)="withdrawFunds()"
                        [disabled]="!writerProgress.is_unlocked || writerWallet.balance <= 0"
                        class="px-6 py-2 bg-wattpad-primary text-white rounded-lg font-medium hover:bg-[#E5274B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        Withdraw ₹{{writerWallet.balance}}
                    </button>
                </div> 
            </div>

            <!-- Unlock Progress -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
                <h2 class="text-xl font-bold mb-4">Eligibility Criteria</h2>
                
                <div class="space-y-6">
                    <!-- Stories Completion -->
                    <div class="eligibility-item pl-4">
                        <div class="flex justify-between items-center mb-2">
                            <div class="flex items-center">
                                <h3 class="font-bold text-lg">Complete 3 Stories</h3>
                                <i *ngIf="isConditionMet(completedStoriesCount, REQUIRED_STORIES)" 
                                   class="fas fa-check-circle text-green-500 ml-2"></i>
                                <i *ngIf="!isConditionMet(completedStoriesCount, REQUIRED_STORIES)" 
                                   class="fas fa-times-circle text-red-500 ml-2"></i>
                            </div>
                            <span class="text-sm font-medium">
                                Completed: {{completedStoriesCount}}/{{REQUIRED_STORIES}}
                            </span>
                        </div>
                        <div class="progress-bar mb-2 bg-gray-200 rounded-full h-2">
                            <div [class]="getProgressColor(calculateProgress(completedStoriesCount, REQUIRED_STORIES))" 
                                 class="progress-fill h-2 rounded-full transition-all duration-300"
                                 [style.width.%]="calculateProgress(completedStoriesCount, REQUIRED_STORIES)">
                            </div>
                        </div>
                        <p class="text-sm text-gray-600">
                            {{ getStoriesProgressMessage() }}
                        </p>
                        
                        <!-- Completed Stories List -->
                        <div *ngIf="writerProgress.completed_stories.length > 0" class="mt-3">
                            <h4 class="font-semibold text-green-600 mb-2">✅ Completed Stories:</h4>
                            <div *ngFor="let story of writerProgress.completed_stories" class="text-sm text-gray-600 ml-4">
                                • {{story.title}} ({{story.episodes_count}} episodes)
                            </div>
                        </div>
                    </div>
                    
                    <!-- 3 Episodes per Story -->
                    <div class="flex items-center">
                        <div [class]="'w-8 h-8 rounded-full flex items-center justify-center mr-3 ' + 
                                    (isConditionMet(writerProgress.episodes_count, REQUIRED_STORIES * REQUIRED_EPISODES_PER_STORY) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800')">
                            <i [class]="isConditionMet(writerProgress.episodes_count, REQUIRED_STORIES * REQUIRED_EPISODES_PER_STORY) ? 'fas fa-check text-xs' : 'fas fa-exclamation text-xs'"></i>
                        </div>
                        <div class="flex-1">
                            <p class="font-medium">9 Total Episodes ({{writerProgress.episodes_count}}/{{REQUIRED_STORIES * REQUIRED_EPISODES_PER_STORY}})</p>
                            <p class="text-sm text-wattpad-light">3 episodes for each of 3 stories</p>
                            <div class="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div class="bg-wattpad-accent h-2 rounded-full" 
                                     [style.width.%]="calculateProgress(writerProgress.episodes_count, REQUIRED_STORIES * REQUIRED_EPISODES_PER_STORY)"></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 1000 Views Condition -->
                    <div class="flex items-center">
                        <div [class]="'w-8 h-8 rounded-full flex items-center justify-center mr-3 ' + 
                                    (isConditionMet(writerProgress.total_views, REQUIRED_VIEWS) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800')">
                            <i [class]="isConditionMet(writerProgress.total_views, REQUIRED_VIEWS) ? 'fas fa-check text-xs' : 'fas fa-exclamation text-xs'"></i>
                        </div>
                        <div class="flex-1">
                            <p class="font-medium">1000 Total Views ({{writerProgress.total_views}}/{{REQUIRED_VIEWS}})</p>
                            <p class="text-sm text-wattpad-light">Achieve 1000 total story views</p>
                            <div class="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div class="bg-wattpad-accent h-2 rounded-full" 
                                     [style.width.%]="calculateProgress(writerProgress.total_views, REQUIRED_VIEWS)"></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 100 Followers Condition -->
                    <div class="flex items-center">
                        <div [class]="'w-8 h-8 rounded-full flex items-center justify-center mr-3 ' + 
                                    (isConditionMet(writerProgress.followers_count, REQUIRED_FOLLOWERS) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800')">
                            <i [class]="isConditionMet(writerProgress.followers_count, REQUIRED_FOLLOWERS) ? 'fas fa-check text-xs' : 'fas fa-exclamation text-xs'"></i>
                        </div>
                        <div class="flex-1">
                            <p class="font-medium">100 Followers ({{writerProgress.followers_count}}/{{REQUIRED_FOLLOWERS}})</p>
                            <p class="text-sm text-wattpad-light">Gain 100 followers</p>
                            <div class="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div class="bg-wattpad-accent h-2 rounded-full" 
                                     [style.width.%]="calculateProgress(writerProgress.followers_count, REQUIRED_FOLLOWERS)"></div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Unlock Message -->
                <div *ngIf="writerProgress.is_unlocked" 
                     class="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div class="flex">
                        <i class="fas fa-trophy text-green-500 text-xl mr-3"></i>
                        <div>
                            <h3 class="font-bold text-green-800 dark:text-green-200">Congratulations!</h3>
                            <p class="text-green-700 dark:text-green-300 text-sm mt-1">
                                You've unlocked your ₹1000 bonus! You can now withdraw funds or transfer to reader wallet.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Locked Message -->
                <div *ngIf="!writerProgress.is_unlocked && writerWallet.locked_balance > 0" 
                     class="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div class="flex">
                        <i class="fas fa-lock text-yellow-500 text-xl mr-3"></i>
                        <div>
                            <h3 class="font-bold text-yellow-800 dark:text-yellow-200">₹{{writerWallet.locked_balance}} Locked</h3>
                            <p class="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                                Complete all eligibility criteria to unlock your bonus amount.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Transaction History -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                <h2 class="text-xl font-bold mb-4">Transaction History</h2>
                
                <div class="overflow-x-auto" *ngIf="writerTransactions.length > 0">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="border-b border-gray-200 dark:border-gray-700">
                                <th class="text-left py-2">#</th>
                                <th class="text-left py-2">Date</th>
                                <th class="text-left py-2">Description</th>
                                <th class="text-right py-2">Amount</th>
                                <th class="text-right py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let transaction of writerTransactions; let i = index" 
                                class="border-b border-gray-100 dark:border-gray-800">
                                <td class="py-3">{{i + 1}}</td>
                                <td class="py-3">{{formatDate(transaction.created_at)}}</td>
                                <td class="py-3">{{transaction.description || 'Earnings'}}</td>
                                <td class="py-3 text-right" 
                                    [class.text-green-500]="transaction.type !== 'withdrawal'"
                                    [class.text-red-500]="transaction.type === 'withdrawal'">
                                    {{transaction.type === 'withdrawal' ? '-' : '+'}}₹{{transaction.amount}}
                                </td>
                                <td class="py-3 text-right">
                                    <span [class]="'px-2 py-1 text-xs rounded-full ' + 
                                                  (transaction.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 
                                                   'bg-yellow-100 text-yellow-800')">
                                        {{transaction.payment_status}}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div *ngIf="writerTransactions.length === 0" class="text-center py-4 text-wattpad-light">
                    No transactions yet
                </div>
            </div>
        </div>

        <!-- Promotion Wallet Content -->
        <div id="promotionWallet" *ngIf="activeTab === 'promotion'" class="wallet-content">
            
            <!-- Promotion Balance Summary -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
                <div class="flex justify-between items-center mb-4">
                    <h2 class="text-xl font-bold">Your Promotion Wallet</h2>
                    <div class="relative">
                        <i class="fas fa-bullhorn text-green-500 text-2xl"></i>
                    </div>
                </div>
                <div class="text-center py-4">
                    <p class="text-wattpad-light mb-1">Current Promotion Balance</p>
                    <p class="text-4xl font-bold text-green-600">{{formatCurrency(promotionBalance)}}</p>
                    <p class="text-sm text-wattpad-light mt-1">₹1.00 per click • Auto-deduct from balance</p>
                </div>
                
                <!-- Add Funds Options -->
                <div class="text-center mt-4">
                    <!-- Direct Recharge (always show) -->
                    <div>
                        <button 
                            (click)="showDirectRecharge = !showDirectRecharge"
                            class="bg-wattpad-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-[#E5274B] transition-colors">
                            <i class="fas fa-plus-circle mr-2"></i>
                            Direct Recharge Promotion
                        </button>
                    </div>
                </div>
            </div>

            <!-- Direct Recharge Section -->
            <div *ngIf="showDirectRecharge" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-bold">💳 Direct Promotion Recharge</h3>
                    <button 
                        (click)="showDirectRecharge = false; directRechargeAmount = 0;"
                        class="text-gray-500 hover:text-gray-700">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <!-- Amount Input for Direct Recharge -->
                <div class="mb-6">
                    <label class="block text-sm font-medium mb-2">Enter Amount to Recharge (₹)</label>
                    <input 
                        type="number" 
                        [(ngModel)]="directRechargeAmount"
                        min="10"
                        max="10000"
                        placeholder="Enter amount"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-wattpad-primary focus:border-transparent dark:bg-gray-700">
                    <p class="text-xs text-gray-500 mt-1">
                        Minimum: ₹10 • Maximum: ₹10,000
                    </p>
                </div>

                <!-- Recharge Button -->
                <button 
                    (click)="rechargePromotionDirect()"
                    [disabled]="directRechargeAmount < 10 || isPromotionLoading"
                    class="w-full bg-wattpad-primary text-white px-4 py-3 rounded-lg font-medium hover:bg-[#E5274B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <i class="fas fa-credit-card mr-2"></i>
                    {{ isPromotionLoading ? 'Processing...' : 'Pay ' + formatCurrency(directRechargeAmount) }}
                </button>
                
                <p class="text-xs text-gray-500 mt-2 text-center">
                    ⚡ Amount will be directly added to your promotion balance
                </p>
            </div>

            <!-- Start Promotion Section -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6" 
                 *ngIf="promotionBalance > 0">
                <h2 class="text-xl font-bold mb-4">🎯 Start Promotion</h2>
                <p class="text-gray-600 mb-4">You have {{formatCurrency(promotionBalance)}} available for promotion. Select a story to start promoting:</p>
                
                <div class="mb-4">
                    <label class="block text-sm font-medium mb-2">Select Story</label>
                    <select 
                        [(ngModel)]="selectedPromotionStory" 
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-wattpad-primary focus:border-transparent dark:bg-gray-700">
                        <option [ngValue]="null">Choose your story...</option>
                        <option *ngFor="let story of userStories" [ngValue]="story">
                            {{story.story_title}} 
                        </option>
                    </select>
                </div>

                <button 
                    (click)="promoteStory()"
                    [disabled]="!selectedPromotionStory || isPromotionLoading"
                    class="w-full bg-wattpad-primary text-white px-4 py-3 rounded-lg font-medium hover:bg-[#E5274B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <i class="fas fa-bullhorn mr-2"></i>
                    {{ isPromotionLoading ? 'Starting Promotion...' : 'Start Promoting This Story' }}
                </button>
                
                <p class="text-xs text-gray-500 mt-2 text-center">
                    ⚡ Clicks will be automatically charged from your promotion balance
                </p>
            </div>

            <!-- Promotion Analytics Section -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                <h2 class="text-xl font-bold mb-4">📊 Your Active Promotions</h2>
                
                <div *ngIf="promotedStories.length > 0" class="space-y-4">
                    <div *ngFor="let promotion of promotedStories" 
                         class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        
                        <!-- Story Header with Status Controls -->
                        <div class="flex justify-between items-start mb-3">
                            <div>
                                <h3 class="font-bold text-lg">{{ promotion.story_title }}</h3>
                                <p class="text-sm text-gray-600">
                                    {{ promotion.genre }} • {{ promotion.episodes_count }} episodes
                                </p>
                            </div>
                            <div class="flex items-center gap-2">
                                <span [class]="'px-3 py-1 text-xs rounded-full font-medium ' + 
                                              (getPromotionStatus(promotion) === 'active' ? 'bg-green-100 text-green-800' : 
                                               getPromotionStatus(promotion) === 'paused' ? 'bg-yellow-100 text-yellow-800' : 
                                               getPromotionStatus(promotion) === 'insufficient_balance' ? 'bg-red-100 text-red-800' : 
                                               'bg-gray-100 text-gray-800')">
                                    {{ getPromotionStatus(promotion) | titlecase }}
                                </span>
                                
                                <div class="flex gap-1" *ngIf="getPromotionStatus(promotion) !== 'insufficient_balance'">
                                    <button 
                                        (click)="updatePromotionStatus(promotion.id, 'active'); $event.stopPropagation()"
                                        [class]="'p-1 rounded text-xs ' + 
                                                (getPromotionStatus(promotion) === 'active' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600')"
                                        title="Activate">
                                        ▶
                                    </button>
                                    <button 
                                        (click)="updatePromotionStatus(promotion.id, 'paused'); $event.stopPropagation()"
                                        [class]="'p-1 rounded text-xs ' + 
                                                (getPromotionStatus(promotion) === 'paused' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-600')"
                                        title="Pause">
                                        ⏸
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- SIMPLIFIED: Only Clicks and Total Spent -->
                        <div class="grid grid-cols-2 gap-4 mb-3">
                            <div class="text-center p-2 bg-gray-50 rounded">
                                <p class="text-sm text-gray-600">Total Clicks</p>
                                <p class="font-bold text-blue-500">{{ promotion.clicks_count || 0 }}</p>
                            </div>
                            <div class="text-center p-2 bg-gray-50 rounded">
                                <p class="text-sm text-gray-600">Total Spent</p>
                                <p class="font-bold text-red-500">{{formatCurrency(promotion.spent || 0)}}</p>
                            </div>
                        </div>
                        
                        <!-- SIMPLE PROGRESS: Only show if there are clicks -->
                        <div class="mb-2" *ngIf="promotion.clicks_count > 0">
                            <div class="flex justify-between text-sm mb-1">
                                <span>Performance</span>
                                <span>{{ promotion.clicks_count }} clicks</span>
                            </div>
                            <div class="w-full bg-gray-200 rounded-full h-2">
                                <div class="bg-green-500 h-2 rounded-full transition-all duration-300" 
                                     [style.width.%]="Math.min((promotion.clicks_count / 100) * 100, 100)">
                                </div>
                            </div>
                        </div>
                        
                        <div class="flex justify-between text-xs text-gray-500">
                            <span>Started: {{ formatDate(promotion.created_at) }}</span>
                            <span>Cost per Click: ₹1.00</span>
                        </div>
                    </div>
                </div>
                
                <!-- Empty State -->
                <div *ngIf="promotedStories.length === 0" class="text-center py-8 text-gray-500">
                    <i class="fas fa-bullhorn text-4xl mb-4 text-gray-300"></i>
                    <p class="text-lg font-medium">No Active Promotions</p>
                    <p class="text-sm mt-2">Add funds and start promoting your stories!</p>
                </div>
            </div>
        </div>
    </div>

    <!-- Recharge Modal -->
    <div *ngIf="showRechargeModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl p-6 w-full max-w-md">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-xl font-bold text-wattpad-primary">Recharge Your Reader Wallet</h3>
                <button (click)="closeRechargeModal()" class="text-gray-500 hover:text-gray-700">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            
            <!-- Plan Selection -->
            <div *ngIf="!selectedPlan">
                <p class="text-gray-600 mb-4">Choose a recharge plan:</p>
                <div class="space-y-3 max-h-96 overflow-y-auto">
                    <div *ngFor="let plan of rechargePlans" 
                         class="border rounded-lg p-4 hover:border-wattpad-primary cursor-pointer transition-colors"
                         (click)="selectPlan(plan)">
                        <div class="flex justify-between items-center">
                            <div>
                                <div class="font-semibold text-lg text-wattpad-primary">₹{{plan.amount}}</div>
                                <div class="text-wattpad-secondary font-medium">{{plan.total_coins}} Coins</div>
                            </div>
                            <div class="text-right">
                                <div class="text-sm text-gray-600">{{plan.description}}</div>
                                <div *ngIf="plan.bonus_coins > 0" class="text-xs text-green-600 font-medium">
                                    +{{plan.bonus_coins}} bonus coins
                                </div>
                            </div>
                        </div>
                        <div class="mt-2 text-xs text-gray-500">
                            Base: {{plan.coins}} coins + Bonus: {{plan.bonus_coins}} coins
                        </div>
                    </div>
                </div>

                <!-- No Plans Message -->
                <div *ngIf="rechargePlans.length === 0" class="text-center py-4 text-gray-500">
                    No recharge plans available at the moment.
                </div>
            </div>
            
            <!-- Selected Plan Confirmation -->
            <div *ngIf="selectedPlan">
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div class="text-center">
                        <div class="text-2xl font-bold text-wattpad-primary">₹{{selectedPlan.amount}}</div>
                        <div class="text-lg text-wattpad-secondary font-semibold">{{selectedPlan.total_coins}} Coins</div>
                        <div class="text-sm text-gray-600">{{selectedPlan.description}}</div>
                        <div *ngIf="selectedPlan.bonus_coins > 0" class="text-sm text-green-600 mt-1">
                            Includes {{selectedPlan.bonus_coins}} bonus coins
                        </div>
                    </div>
                </div>
                
                <div class="flex space-x-3">
                    <button 
                        (click)="selectedPlan = null"
                        class="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors">
                        Back
                    </button>
                    <button 
                        (click)="processRecharge()"
                        [disabled]="paymentProcessing"
                        class="flex-1 bg-wattpad-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                        {{ paymentProcessing ? 'Processing...' : 'Pay ₹' + selectedPlan.amount }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</main>